import { Component, Mixins } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { actions, NAVIGATION_NAMESPACE } from '../../store/modules/navigation';
import type { OmitFirst } from '../../types';
import { RouteName } from '../../routes/routes.constants';
import {
    FeatureEnabledV2,
    getFeatureValueV2,
    MagicConfigMixin,
    RoutablePage,
} from '@gig/magic-common';
import { Feature } from '../../util';
import {
    FeatureSettings,
    GAMES_SCROLL_DOWN,
    IS_MOBILE_APP_QUERY,
    MESSAGING_LOADED,
    MESSAGING_UNREAD_LIST,
    REWARD_COUNT,
    USER_LOGGED_IN_UPDATED,
} from '@gig/magic-messages';
import { ISubscription } from '@gig/magic-message-bus/dist/base-bus';
import { NotLocalized } from '@gig/magic-sdk';
import { IMessage } from '@gig/magic-sdk/dist/adapter/messages/message';

const navigationModule = namespace(NAVIGATION_NAMESPACE);

/**
 * The bottom navigation component displays the lower
 * mobile navigation items.
 */
@Component
export default class BottomNavigation extends Mixins(MagicConfigMixin) {
    RouteName = RouteName;

    @FeatureEnabledV2({ slug: Feature.DYNAMIC_OPTION_MOBILE_BOTTOM_MENU })
    isDynamicMenuOptionEnabled: boolean = false;

    @FeatureEnabledV2({
        slug: Feature.ANIMATE_COIN_SHOP_BUTTON,
    })
    animateCoinShopButton = false;

    /**
     * is top navigation enabled
     */
    @FeatureEnabledV2({
        slug: Feature.TOP_NAVIGATION,
    })
    isTopNavigationEnabled = true;
    topNavigationSettings: string = '';

    /**
     * is top navigation enabled
     */
    @FeatureEnabledV2({
        slug: Feature.TOP_NAVIGATION,
    })

    /**
     * Number of unread messages
     */
    messages = 0;

    /**
     * Number of pending rewards
     */
    rewards: number = 0;

    /**
     * Emits the action to toggle the menu.
     */
    @navigationModule.Action('toggleMainMenu')
    toggleMenuAction!: OmitFirst<typeof actions.toggleMainMenu>;

    isLoggedIn = false;

    private messageSubscription: ISubscription | undefined;
    private rewardCountSubscription: ISubscription | undefined;
    private loggedInSubscription: ISubscription | undefined;
    private videoTimeInterval: ReturnType<typeof setTimeout> | null = null;
    private videoTimeIntervalTime: number = 5000; // 3000ms + 2000ms animation time

    public isMobileApp: boolean = false;

    async created() {
        this.topNavigationSettings = await getFeatureValueV2(
            Feature.TOP_NAVIGATION,
            '',
        );

        this.isMobileApp =
            (await window.queryBus.query(IS_MOBILE_APP_QUERY)) ?? false;
        this.messageSubscription = window.eventBus.subscribe<boolean>(
            MESSAGING_LOADED,
            async (event) => {
                if (!event.payload) {
                    this.messages = 0;
                    return;
                }

                this.messages =
                    (
                        await window.queryBus.query<
                            Array<IMessage<NotLocalized>>
                        >(MESSAGING_UNREAD_LIST)
                    )?.length ?? 0;
            },
            { replay: true },
        );

        this.topNavigationSettings = await getFeatureValueV2(
            Feature.TOP_NAVIGATION,
            '',
        );

        this.rewardCountSubscription = window.eventBus.subscribe<number>(
            REWARD_COUNT,
            (e) => {
                this.rewards = e.payload ?? 0;
            },

            { replay: true },
        );

        this.loggedInSubscription = window.eventBus.subscribe<boolean>(
            USER_LOGGED_IN_UPDATED,
            (e) => {
                this.isLoggedIn = e.payload ?? false;
            },

            { replay: true },
        );
        this.startVideoInterval();
    }

    destroyed() {
        this.messageSubscription?.unsubscribe();
        this.rewardCountSubscription?.unsubscribe();
        this.loggedInSubscription?.unsubscribe();
        this.clearVideoTimeout();
    }

    /**
     * Toggles open/close the menu.
     */
    toggleMenu(event: Event) {
        event.preventDefault();
        this.toggleMenuAction();
    }

    gamesLinkClicked() {
        const alreadyOnHome = this.$route.name === RouteName.HOME;
        const alreadyOnGames = this.$route.name === RouteName.GAMES;

        if (alreadyOnHome || alreadyOnGames) {
            window.commandBus.publish(GAMES_SCROLL_DOWN);
            return;
        }
    }

    routeToShop() {
        this.routeTo({ route: RoutablePage.SWEEPSTAKES_SHOP });
    }

    private startVideoInterval() {
        this.clearVideoTimeout();
        this.videoTimeInterval = setInterval(() => {
            const video = this.$refs.buyButtonVideo as HTMLVideoElement;
            if (video) {
                video.currentTime = 0;
                video.play().catch(() => {
                    // Handle any play errors silently
                });
            }
        }, this.videoTimeIntervalTime);
    }

    private clearVideoTimeout() {
        if (this.videoTimeInterval) {
            clearInterval(this.videoTimeInterval);
            this.videoTimeInterval = null;
        }
    }

    get isBingoLobby() {
        return this.$route.name === RouteName.BINGO_LOBBY;
    }

    get shouldDisplayRewards() {
        return (
            this.isDynamicMenuOptionEnabled && this.isLoggedIn && this.rewards
        );
    }

    get shouldDisplayMessages() {
        return (
            !this.isDynamicMenuOptionEnabled ||
            (this.isLoggedIn && this.messages)
        );
    }

    get isTopNavigationWithBottomEnabled() {
        return (
            this.isTopNavigationEnabled &&
            this.topNavigationSettings.includes(
                FeatureSettings.BOTTOM_NAVIGATION_ENABLED,
            )
        );
    }
    get isTopNavigationRightSide() {
        return (
            this.isTopNavigationEnabled &&
            !this.topNavigationSettings.includes(FeatureSettings.LEFT_SIDE_MENU)
        );
    }

    get shouldDisplayBottomBar() {
        if (this.isMobileApp) {
            return false;
        }
        if (this.isTopNavigationWithBottomEnabled) {
            return true;
        }
        if (this.isBingoLobby || this.isTopNavigationEnabled) {
            return false;
        }

        return this.$route.name !== RouteName.SPORTSBOOK;
    }
}
